import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { currentUserRole } from 'utility/cache';
import { UserRoles } from 'common/graphql/__generated__/UserRoles';
import { useAuth0 } from '@auth0/auth0-react';
import { StringParam, useQueryParam } from 'use-query-params';
import {
  SetPreferredRole,
  SetPreferredRoleVariables,
} from 'common/graphql/__generated__/SetPreferredRole';
import { config } from '../../config';
import { storePath } from 'utility/redirect';

import SET_PREFERRED_ROLE from 'common/graphql/SetPreferredRole.graphql';
import ROLES from 'common/graphql/UserRoles.graphql';
import { LoggingIn } from 'common/components/LoggingIn';

const RoleResolver: React.FC = () => {
  const {
    isAuthenticated,
    loginWithRedirect,
    isLoading: auth0Loading,
    logout: auth0Logout,
  } = useAuth0();

  const [targetProfileId, setTargetProfileId] = useQueryParam(
    'profile',
    StringParam
  );

  const [setPreferredRole] = useMutation<
    SetPreferredRole,
    SetPreferredRoleVariables
  >(SET_PREFERRED_ROLE, { errorPolicy: 'all' });

  // don't fetch roles until user is authenticated
  const { data: userRolesData, error } = useQuery<UserRoles>(ROLES, {
    skip: !isAuthenticated || auth0Loading,
  });

  const logout = useCallback(
    () =>
      auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
        clientId: config!.auth0.clientId,
      }),
    [auth0Logout]
  );

  // We should handle this properly, but for now, force logout if there is a fetch roles error.
  // This could happen if eg. there are no roles.
  useEffect(() => {
    if (error) logout();
  }, [logout, error]);

  // redirect to auth0 if user isn't authenticated
  useEffect(() => {
    if (!auth0Loading && !isAuthenticated) {
      loginWithRedirect({ appState: storePath() });
    }
  }, [auth0Loading, isAuthenticated, loginWithRedirect]);

  // fetch and resolve current role
  const resolved = useRef(false);
  useEffect(() => {
    const roles = userRolesData?.availableRoles;
    if (!roles || resolved.current) return;
    resolved.current = true;

    const foundTargetProfile = targetProfileId
      ? roles.find((role) => role.id === targetProfileId)
      : undefined;

    // No roles, or we expected to find a user profile passed through url params but didn't,
    // assume it's under a separate auth0 user and force logout.
    if (roles.length === 0 || (targetProfileId && !foundTargetProfile)) {
      logout();
      return;
    }

    // priority ordering: role from URL param, then preferred role, then first role
    const role =
      foundTargetProfile ?? roles.find((r) => r.isPreferred) ?? roles[0];

    if (!role.isPreferred)
      setPreferredRole({ variables: { userRoleId: role.id } });

    setTargetProfileId(undefined, 'replaceIn');
    currentUserRole(role);
  }, [
    userRolesData,
    targetProfileId,
    setTargetProfileId,
    setPreferredRole,
    logout,
  ]);

  // we're still waiting for authentication or role fetch
  return <LoggingIn />;
};

export const LoginWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const role = useReactiveVar(currentUserRole);

  // ready for the app
  if (role) return <>{children}</>;

  return <RoleResolver />;
};
